"use client";

import { useEffect, useId, useRef, useState } from "react";

/**
 * Merender HTML+JS mentah buatan admin di dalam <iframe srcDoc> dengan
 * sandbox="allow-scripts …" TANPA allow-same-origin → skrip berjalan di origin
 * OPAQUE: tak bisa membaca document.cookie / localStorage / DOM parent tenant,
 * sehingga tak bisa mencuri sesi pengunjung. Inilah alasan html boleh disimpan
 * mentah (tanpa sanitasi) di server. Dipakai landing 'custom_html' & store 'embed'.
 *
 * Tinggi: mode 'auto' memakai postMessage dari bootstrap yang kita suntik
 * (ResizeObserver + load); mode 'fixed' pakai heightPx.
 */
export default function SandboxedHtmlFrame({
  html,
  heightMode = "auto",
  heightPx,
  title = "Konten kustom",
}: {
  html: string;
  heightMode?: "auto" | "fixed";
  heightPx?: number;
  title?: string;
}) {
  const frameId = useId();
  const iframeRef = useRef<HTMLIFrameElement | null>(null);
  const fixed = heightMode === "fixed";
  const [height, setHeight] = useState<number>(fixed ? heightPx ?? 400 : 240);

  useEffect(() => {
    if (fixed) return;
    function onMessage(e: MessageEvent) {
      if (iframeRef.current && e.source !== iframeRef.current.contentWindow) return;
      const d = e.data as { __gcFrame?: string; height?: number } | null;
      if (d && d.__gcFrame === frameId && typeof d.height === "number") {
        setHeight(Math.min(Math.max(Math.ceil(d.height), 80), 8000));
      }
    }
    // BUKAN `window` top-level — pratinjau admin me-render pohon komponen ini
    // lewat createPortal ke DALAM iframe device-preview-nya sendiri
    // (DevicePreviewFrame). Bootstrap di srcDoc memanggil `parent.postMessage`,
    // dan `parent` di situ berarti window IFRAME PREVIEW itu (DOM parent
    // sungguhan iframe ini), bukan window browser teratas — padahal kode efek
    // React ini tetap berjalan di realm window teratas walau DOM-nya di-portal.
    // `ownerDocument.defaultView` dari iframe ini sendiri selalu window yang
    // BENAR menerima pesan itu, di preview bersarang maupun halaman publik.
    const listenWindow = iframeRef.current?.ownerDocument?.defaultView ?? window;
    listenWindow.addEventListener("message", onMessage);
    return () => listenWindow.removeEventListener("message", onMessage);
  }, [frameId, fixed]);

  const bootstrap = fixed
    ? ""
    : `<script>(function(){var ID=${JSON.stringify(frameId)};function post(){var h=Math.max(document.documentElement.scrollHeight,document.body?document.body.scrollHeight:0);parent.postMessage({__gcFrame:ID,height:h},'*');}if(document.readyState!=='loading')post();window.addEventListener('load',post);window.addEventListener('resize',post);try{new ResizeObserver(post).observe(document.documentElement);}catch(e){}var n=0,t=setInterval(function(){post();if(++n>20)clearInterval(t);},500);})();</script>`;

  const srcDoc =
    `<!doctype html><html><head><meta charset="utf-8">` +
    `<meta name="viewport" content="width=device-width, initial-scale=1">` +
    `<base target="_blank"><style>html,body{margin:0;padding:0}</style></head>` +
    `<body>${html}${bootstrap}</body></html>`;

  return (
    <iframe
      ref={iframeRef}
      title={title}
      srcDoc={srcDoc}
      sandbox="allow-scripts allow-popups allow-popups-to-escape-sandbox allow-forms"
      loading="lazy"
      className="block w-full border-0"
      style={{ height }}
      scrolling="no"
    />
  );
}
